import React, { useState, useEffect } from 'react';
import GirlsCornerEditor from './GirlsCornerEditor.tsx';

interface Photo {
  id: string;
  fileName: string;
  originalFileName: string;
  filePath: string;
  relativePath?: string;
  displayOrder: number;
}

interface Post {
  id: string;
  title: string;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  creator: {
    firstName: string;
    lastName: string;
  };
  photos: Photo[];
}

export default function GirlsCornerManagement() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/girlscorner/admin/all', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setPosts(data.data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPost(null);
    setShowEditor(true);
  };

  const handleEdit = (post: Post) => {
    setEditingPost(post);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingPost(null);
  };

  const handleSaveSuccess = () => {
    setShowEditor(false);
    setEditingPost(null);
    fetchPosts();
  };

  const handleActivate = async (postId: string) => {
    if (!window.confirm('Set this post as the active post? This will deactivate all other posts.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/girlscorner/${postId}/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        fetchPosts();
      } else {
        alert('Failed to activate post: ' + data.message);
      }
    } catch (error) {
      console.error('Error activating post:', error);
      alert('Failed to activate post');
    }
  };

  const handleDelete = async (postId: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This will also delete all associated photos.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/girlscorner/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        fetchPosts();
      } else {
        alert('Failed to delete post: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  const toggleExpand = (postId: string) => {
    setExpandedPostId(expandedPostId === postId ? null : postId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (showEditor) {
    return (
      <GirlsCornerEditor
        post={editingPost}
        onClose={handleCloseEditor}
        onSuccess={handleSaveSuccess}
      />
    );
  }

  return (
    <div className="px-12 py-10">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">⭐ Girls' Corner Management</h1>
          <p className="text-gray-600 mt-2">Create and manage weekly updates for families and alumni</p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-ggaa-green text-white px-6 py-3 rounded-lg font-semibold hover:bg-ggaa-dark transition-colors shadow-lg"
        >
          + Create New Post
        </button>
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
          <span className="ml-4 text-gray-600">Loading posts...</span>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">📝</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Posts Yet</h3>
          <p className="text-gray-600 mb-6">Create your first Girls' Corner post to get started</p>
          <button
            onClick={handleCreate}
            className="bg-ggaa-green text-white px-6 py-3 rounded-lg font-semibold hover:bg-ggaa-dark transition-colors"
          >
            Create First Post
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div
              key={post.id}
              className={`bg-white rounded-xl shadow-lg border-2 ${
                post.isActive ? 'border-green-500' : 'border-gray-200'
              } overflow-hidden`}
            >
              {/* Post Header */}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{post.title}</h3>
                      {post.isActive && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Posted by {post.creator.firstName} {post.creator.lastName}</span>
                      <span>•</span>
                      <span>{formatDate(post.createdAt)}</span>
                      {post.photos.length > 0 && (
                        <>
                          <span>•</span>
                          <span>📸 {post.photos.length} photo{post.photos.length !== 1 ? 's' : ''}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => toggleExpand(post.id)}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
                    >
                      {expandedPostId === post.id ? '▲ Hide' : '▼ Preview'}
                    </button>
                    {!post.isActive && (
                      <button
                        onClick={() => handleActivate(post.id)}
                        className="px-4 py-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors text-sm font-medium"
                      >
                        Set as Active
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(post)}
                      className="px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(post.id, post.title)}
                      className="px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedPostId === post.id && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="prose max-w-none mb-4">
                      <div className="whitespace-pre-wrap text-gray-700">{post.content}</div>
                    </div>
                    
                    {/* Photos */}
                    {post.photos.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Photos:</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {post.photos.map((photo) => (
                            <div key={photo.id} className="relative group">
                              <img
                                src={`/uploads/girlscorner/${photo.fileName}`}
                                alt={photo.originalFileName}
                                className="w-full h-32 object-cover rounded-lg border border-gray-200"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                                <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                  {photo.originalFileName}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

